/* @flow */
import moment from 'moment';

import navigation from 'services/NavigationService';
import { apiService } from 'services/ServiceRegistry';

import Form from '../_.extend';

const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  
  return new Promise(resolve => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

// An empty selection must resolve to an empty array (not `['']`) so the API
// can tell "remove all Trading Accounts" apart from "one blank account id".
const toAccountIds = (value: string): string[] => (value ? value.split(',') : []);

export const makeFields = (t: Function, mode: string, brand?: Object) => {
  return [
    {
      name: 'details',
      label: t('general:reporting.brand_form.labels.details'),
      fields: [
        {
          name: 'name',
          label: t('general:reporting.brand_form.labels.name'),
          rules: 'required|string',
          value: mode === 'edit' && brand ? brand.name : null,
        },
      ],
    },
    {
      name: 'brandLogo',
      label: t('general:reporting.brand_form.labels.brand_logo'),
      fields: [
        {
          name: 'file',
          label: t('general:reporting.brand_form.labels.file'),
          rules: '',
        },
      ],
    },
    {
      name: 'accounts',
      label: t('general:reporting.brand_form.labels.accounts'),
      fields: [
        {
          name: 'chooseAccounts',
          label: t('general:reporting.brand_form.labels.choose_accounts'),
          rules: 'string',
          type: 'textfield',
          value: mode === 'edit' && brand ? brand.account_ids.join(',') : '',
          extra: {
            selectID: null,
          },
        },
      ],
    },
  ];
};

class BrandFormCreate extends Form {}

class BrandFormEdit extends Form {}

// Returns true if the error was a duplicate-brand-name conflict (and updates localState accordingly).
// Returns false otherwise, leaving the caller to handle the generic error case.
const handleDuplicateBrandError = (err: Object, localState: Object): boolean => {
  if (!err.data || !err.data.duplicate_brand_id) {
    return false;
  }
  localState.showResponseMessage = false;
  localState.hasError = false;
  localState.duplicateBrand = {
    id: err.data.duplicate_brand_id,
    name: err.data.duplicate_brand_name,
  };
  return true;
};

export const brandCreate = (fields: Array<*>, localState: Object, t: Function) => {
  const hooks = {
    onSuccess: async function(form) {
      localState.firstLoad = false;
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;
      
      const jsonObj: { name: string, account_ids: string[], logo?: string } = {
        name: form.$('details.name').value,
        account_ids: toAccountIds(form.$('accounts.chooseAccounts').value),
      };
      
      const logo = form.$('brandLogo.file').value;
      if (logo) {
        jsonObj.logo = await blobToBase64(logo);
      }
      
      apiService
      .createResource('brands', jsonObj)
      .then(response => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.save_success');
        
        setTimeout(() => {
          localState.showResponseMessage = false;
          /*eslint-disable max-nested-callbacks*/
          setTimeout(() => {
            // Snackbar animation 225ms
            navigation.goBack();
          }, 225);
        }, 1200);
        
        return response;
      })
      .catch(err => {
        localState.isLoading = false;
        localState.hasSuccess = false;
        
        if (handleDuplicateBrandError(err, localState)) {
          return;
        }
        else {
          localState.responseMessage = err.message || t('general:messages.save_error');
          localState.hasError = true;
        }
      });
    },
    onError(form) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };
  
  return new BrandFormCreate({ fields }, { hooks, name: t('general:actions.create_new_brand') });
};

export const brandEdit = (fields: Array<*>, brand: Object, localState: Object, t: Function) => {
  localState.selectedTradingAccounts = brand.account_ids.join(',');
  
  const hooks = {
    async onSuccess(form) {
      localState.firstLoad = false;
      localState.isLoading = true;
      localState.responseMessage = t('general:messages.saving_form');
      localState.showResponseMessage = true;
      localState.hasError = false;
      localState.hasSuccess = true;
      
      const jsonObj: { name: string, account_ids: string[], logo?: string } = {
        name: form.$('details.name').value,
        account_ids: toAccountIds(form.$('accounts.chooseAccounts').value),
      };
      
      const logo = form.$('brandLogo.file').value;
      if (logo) {
        jsonObj.logo = await blobToBase64(logo);
      }
      
      apiService
      .putResource(`brands/${brand.id}`, jsonObj)
      .then(response => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.save_success');
        
        setTimeout(() => {
          localState.showResponseMessage = false;
          /*eslint-disable max-nested-callbacks*/
          setTimeout(() => {
            // Snackbar animation 225ms
            navigation.goBack();
          }, 225);
        }, 1200);
        
        return response;
      })
      .catch(err => {
        localState.isLoading = false;
        localState.hasSuccess = false;
        
        if (handleDuplicateBrandError(err, localState)) {
          return;
        }
        else {
          localState.responseMessage = err.message || t('general:messages.save_error');
          localState.hasError = true;
        }
        
      });
    },
    onError(form: Object) {
      localState.isLoading = false;
      localState.responseMessage = t('general:messages.form_error');
      localState.showResponseMessage = true;
      localState.hasSuccess = false;
      localState.hasError = true;
    },
  };
  
  return new BrandFormEdit({ fields }, { hooks, name: t('general:actions.edit_brand') });
};
